/*
 * -----------------------------------------------------------------------
 *  <copyright file="column-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { ChartOptions } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import { getChartEvents } from '@components-clean/core/charts/shared/utils';
import {
    IOpraColumnChart,
    HC_LINE_STYLES,
    HC_RAG_NAMES,
    HC_TREND_LABELS,
    HC_STACKING_OPTIONS,
    OpraColours,
    HC_COLORS_STANDARD,
    HC_COLORS_SAND_SIGNIKA,
    OpraVerticalAlign,
    FormatNumberForChart,
} from 'interfaces/opra-chart.interface';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import { PanelFrameContext } from 'views/components/core/panel-frame';

const ColumnChart = (chartProps: IOpraColumnChart) => {
    const {
        categories,
        getRef,
        secondaryKey,
        showLegend,
        xAxisTitle,
        yAxisTitle,
        series,
        plotTitle,
        formatTooltip,
        type,
        stacking,
        horizontalLines,
        setLoadingComplete,
        hidePlotTitle,
        displayFullScreenAndPrintTitle = false,
    } = chartProps;
    const [options, setOptions] = useState<ChartOptions | null>(null);
    const charts = useRef(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        /* This is required due to the stacked bar having dropdown fields on the Risk Dashboard. */
        style: {
            height: window.location.pathname.includes('risk-assessment') ? '300px' : '350px',
        },
    });

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: String(height?.height),
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) getRef(charts?.current);
    }, [charts.current, options]);

    function getItemWidth() {
        const longestName = series.reduce((a, b) => (a.name.length > b.name.length ? a : b)).name.length;
        const maxCharacterWidth = 11;
        const legendPointWith = 15;

        const minWidth = Math.max(longestName * maxCharacterWidth + legendPointWith, 100);

        return Math.min(minWidth, 200);
    }

    useEffect(() => {
        setOptions({
            colors: HC_COLORS_SAND_SIGNIKA,
            credits: {
                enabled: false,
            },
            chart: {
                type: 'column',
                zoomType: 'xy',
                events: displayFullScreenAndPrintTitle
                    ? getChartEvents(false, false, plotTitle, undefined, {
                          fullScreenOptions: {
                              title: { text: plotTitle, style: { fontWeight: '600' } },
                          },
                          printOptions: {
                              title: { text: plotTitle, style: { fontWeight: '500' } },
                          },
                      })
                    : undefined,
            },
            exporting: {
                enabled: !getRef,
                chartOptions: { title: { text: plotTitle, style: { fontWeight: 'semi-bold' } } },
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            title: hidePlotTitle ? {} : { text: plotTitle },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                          text: secondaryKey.text,
                          useHTML: true,
                          verticalAlign:
                              secondaryKey.position === OpraVerticalAlign.top
                                  ? 'top'
                                  : secondaryKey.position === OpraVerticalAlign.middle
                                    ? 'middle'
                                    : 'bottom',
                          style: { display: (showLegend ?? true) ? undefined : 'none' },
                      },
            legend: {
                enabled: showLegend ?? true,
                symbolHeight: 9,
                symbolWidth: 10,
                symbolRadius: 2,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    textOverflow: 'ellipsis',
                },
                maxHeight: 70,
                itemWidth: getItemWidth(),
            },
            xAxis: {
                categories,
                title: {
                    text: xAxisTitle || null,
                },
            },
            tooltip: {
                formatter() {
                    const self: any = this;
                    if (formatTooltip) return formatTooltip(self);

                    let result = `<b>${self.point.category}</b>`;
                    result += `<br/>${self.series.name}`;
                    if (self.point.site) result += `<br/>Site: ${self.point.site}`;
                    if (self.point.rag) result += `<br/>RAG: ${HC_RAG_NAMES[self.point.rag]}`;
                    if (self.point.trend) result += `<br/>Trend: ${HC_TREND_LABELS[self.point.trend]}`;
                    if (self.point.score) result += `<br/>Score: ${FormatNumberForChart(self.point.score)}`;
                    if (self.series.userOptions && self.series.userOptions.custom) {
                        if (self.series.userOptions.custom.site)
                            result += `<br/>Site: ${self.series.userOptions.custom.site}`;
                        if (self.series.userOptions.custom.numSubjects)
                            result += `<br/># Subjects: ${FormatNumberForChart(
                                self.series.userOptions.custom.numSubjects,
                            )}`;
                    }

                    if (self.series.userOptions.custom.numRisks)
                        result += `<br/># Risks: ${FormatNumberForChart(self.point.y)}`;
                    else result += `<br/>Value: ${FormatNumberForChart(self.point.y)}`;

                    if (
                        type === PanelConstants.graphTypes.STACKEDCOLUMN ||
                        type === PanelConstants.graphTypes.STACKED_COLUMN
                    )
                        result += `<br/>Percent: ${FormatNumberForChart((self.y / self.point.stackTotal) * 100)}`;

                    return result;
                },
            },
            plotOptions: {
                series: {
                    stacking: HC_STACKING_OPTIONS[stacking ?? 0],
                },
                column: {
                    zIndex: 3,
                },
            },
            yAxis: {
                title: {
                    text: yAxisTitle,
                },
                plotLines:
                    horizontalLines === undefined
                        ? undefined
                        : horizontalLines.map((pl) => ({
                              color: pl.color,
                              dashStyle: HC_LINE_STYLES[pl.style],
                              label: { text: pl.name },
                              value: pl.y,
                              zIndex: 1,
                          })),
            },
            series:
                series?.map((s) => ({
                    name: s.name,
                    color: s.color,
                    data: s.values,
                    custom: s.custom,
                })) ?? [],
        } as ChartOptions);
    }, [chartProps]);

    if (options === null) {
        if (setLoadingComplete) setLoadingComplete();

        return <div>No data available.</div>;
    }

    return (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style} // Mending size after fullscreen and setting with for expand chart functionality
        />
    );
};

export default ColumnChart;
